import { api } from "../../lib/api/api";
import { path } from "../../lib/path/path";
import { args } from "../../lib/args/args";
import { expectShape } from "../../lib/expect/expect-shape";
import { is } from "../../lib/is/is";
import { out } from "../../lib/output/out";
import { ACTIONS } from "@/commands/keys/keys-constants";
import { createKey } from "@/commands/key/create-key";
import type { ApiOutputs } from "@/lib/api/api-outputs";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";

/**
 * Human sessions only, by design: a key cannot mint or revoke a key, so the
 * whole group fails with a 403 when HEPHA_API_KEY is set.
 */
export async function keysCommand(parsed: ParsedArgs): Promise<void> {
  const action = args.action(parsed, "keys", ACTIONS, "list");
  const asJson = parsed.booleans.has("json");

  if (action === "list") {
    args.expect(parsed, { positional: 1 });
    const result = expectShape<ApiOutputs["keys"]["list"]>(
      await api("/keys"),
      "GET /keys",
      { keys: is.array },
    );
    if (asJson) {
      out.json(result);
      return;
    }
    if (result.keys.length === 0) {
      out.line("No keys. Create one with `hepha keys create <name>`.");
      return;
    }
    out.table([
      [
        out.paint("ID", "dim"),
        out.paint("NAME", "dim"),
        out.paint("PREFIX", "dim"),
        out.paint("SCOPES", "dim"),
      ],
      ...result.keys.map((key) => [
        out.cell(key.id),
        out.cell(key.name ?? "—"),
        out.cell(key.start ?? "—"),
        key.scopes.join(" "),
      ]),
    ]);
    return;
  }

  if (action === "create") {
    return await createKey(parsed, asJson);
  }

  args.expect(parsed, { positional: 2 });
  const keyId = args.required(parsed, 1, "keyId");
  const result = await api<ApiOutputs["keys"]["revoke"]>(path`/keys/${keyId}`, {
    method: "DELETE",
  });
  if (asJson) {
    out.json(result);
    return;
  }
  out.line(`${out.paint("✓", "green")} Revoked ${out.cell(keyId)}`);
}
